/**
 * JoinCandidateGroups — the auto-ON groups of the [+ add join] list: tables
 * linked by a real foreign-key constraint, then tables linked by a loose
 * relationship declared in MetaInfo. The two read differently on purpose — a
 * loose relationship is the user's own declaration (and may carry source /
 * target filters that become part of the ON), not a constraint the database
 * enforces — so each gets its own heading and badge.
 */

import { CommandGroup, CommandItem } from '../../../../../components/ui/command';
import type { JoinCandidate } from '../../../../sql-resolver/types/schema';
import { joinSuggestionKey, joinSuggestionLabel } from '../../edit/edits/joinHelpers';
import { vqeCopy } from '../../ux/vqeCopy';
import { VqeTooltip } from '../common/VqeTooltip';

export interface JoinCandidateGroupsProps {
  readonly testId: string;
  readonly candidates: readonly JoinCandidate[];
  readonly onPick: (candidate: JoinCandidate) => void;
}

const BADGE =
  'shrink-0 rounded px-1 py-0 text-[9px] font-semibold tracking-wide';

export function JoinCandidateGroups({ testId, candidates, onPick }: JoinCandidateGroupsProps) {
  const real = candidates.filter((c) => !c.loose);
  const loose = candidates.filter((c) => c.loose);
  return (
    <>
      {real.length > 0 ? (
        <CommandGroup heading="Linked by foreign key">
          {real.map((c) => (
            <CandidateItem key={joinSuggestionKey(c)} testId={testId} candidate={c} onPick={onPick} />
          ))}
        </CommandGroup>
      ) : null}
      {loose.length > 0 ? (
        <CommandGroup heading="Linked by loose relationship" data-testid={`${testId}-loose-group`}>
          {loose.map((c) => (
            <CandidateItem key={joinSuggestionKey(c)} testId={testId} candidate={c} onPick={onPick} />
          ))}
        </CommandGroup>
      ) : null}
    </>
  );
}

function CandidateItem({
  testId,
  candidate,
  onPick,
}: {
  readonly testId: string;
  readonly candidate: JoinCandidate;
  readonly onPick: (candidate: JoinCandidate) => void;
}) {
  const key = joinSuggestionKey(candidate);
  const loose = candidate.loose;
  const filtered = !!loose && ((loose.sourceFilter?.length ?? 0) > 0 || (loose.targetFilter?.length ?? 0) > 0);
  return (
    <CommandItem
      value={`${joinSuggestionLabel(candidate)} ${key}`}
      data-testid={`${testId}-item-${key}`}
      data-join-link={loose ? 'loose' : 'fk'}
      onSelect={() => onPick(candidate)}
      className="gap-2"
    >
      <span className="flex-1 truncate font-mono">{joinSuggestionLabel(candidate)}</span>
      {loose ? (
        <VqeTooltip content={filtered ? vqeCopy.picker.looseJoinFilteredHint : vqeCopy.picker.looseJoinHint}>
          <span
            className={`${BADGE} bg-amber-500/15 text-amber-700 dark:text-amber-400`}
            data-testid={`${testId}-badge-loose`}
          >
            Loose FK
          </span>
        </VqeTooltip>
      ) : (
        <span
          className={`${BADGE} bg-sky-500/15 text-sky-600 dark:text-sky-400`}
          title="Linked by a foreign key — ON is auto-derived"
        >
          FK
        </span>
      )}
    </CommandItem>
  );
}
